import CheckIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import RevertIcon from "@mui/icons-material/ReplayRounded";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useState } from "react";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ObjectFieldValues } from "../../../../api/types/objectTypes";
import ObjectsListContext from "../../../entityFields/grid/ObjectsListContext";
import InvoiceActionDialog from "./InvoiceActionDialog";
import type { InvoiceAction } from "./invoiceGridActions";
import { isInvoiceActionAvailable } from "./invoiceGridActions";
import InvoicesGridContext from "./InvoicesGridContext";
import useInvoiceDocumentsDownload from "./useInvoiceDocumentsDownload";

interface Props {
  invoice: ObjectFieldValues;
}

const InvoicesGridRowActions = ({ invoice }: Props) => {
  const apiRef = useGridApiContext();
  const { refreshObjects } = useDefinedContext(ObjectsListContext, "ObjectsListContext");
  const eligibility = useDefinedContext(InvoicesGridContext, "InvoicesGridContext");
  const { downloading, downloadInvoiceDocuments } = useInvoiceDocumentsDownload();

  const [actionToConfirm, setActionToConfirm] = useState<InvoiceAction>();

  const isHidden = (action: InvoiceAction) => !isInvoiceActionAvailable(invoice, action, eligibility);

  const handleExecuted = (succeededInvoiceIds: string[]) => {
    if (succeededInvoiceIds.length > 0 && apiRef.current.isRowSelected(invoice.id)) {
      apiRef.current.selectRow(invoice.id, false);
    }
    refreshObjects();
  };

  return (
    <>
      <ActionsMenuButton
        items={[
          {
            label: "Download",
            icon: <DownloadIcon />,
            disabled: downloading,
            onClick: () => downloadInvoiceDocuments([invoice.id]),
          },
          {
            label: "Submit for Approval",
            icon: <CheckIcon color="primary" />,
            hidden: isHidden("SubmitInvoiceForApproval"),
            onClick: () => setActionToConfirm("SubmitInvoiceForApproval"),
          },
          {
            label: "Approve",
            icon: <CheckIcon color="success" />,
            hidden: isHidden("Approve"),
            onClick: () => setActionToConfirm("Approve"),
          },
          {
            label: "Reject",
            icon: <CloseIcon color="error" />,
            hidden: isHidden("Reject"),
            onClick: () => setActionToConfirm("Reject"),
          },
          {
            label: "Return to Draft",
            icon: <RevertIcon />,
            hidden: isHidden("ReturnInvoiceToDraft"),
            onClick: () => setActionToConfirm("ReturnInvoiceToDraft"),
          },
          {
            label: "Delete",
            icon: <DeleteIcon color="error" />,
            hidden: isHidden("DeleteInvoice"),
            onClick: () => setActionToConfirm("DeleteInvoice"),
          },
        ]}
      />
      {actionToConfirm !== undefined && (
        <InvoiceActionDialog
          action={actionToConfirm}
          invoices={[invoice]}
          onClose={() => setActionToConfirm(undefined)}
          onExecuted={handleExecuted}
        />
      )}
    </>
  );
};

export default InvoicesGridRowActions;
